import { Suspense, lazy } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { AuthProvider } from '@/contexts/AuthContext';
import { UserMessagesProvider } from '@/contexts/UserMessagesContext';
import { LocalLLMProvider } from '@/contexts/LocalLLMContext';
import { SsrPayloadProvider } from '@/lib/ssr/context';
import { isPublicSsrPath } from '@/lib/ssr/routes';
import type { SsrPayload } from '@/types/ssr';

import PublicSiteShell from '@/components/layout/PublicSiteShell';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import RouteAnalyticsTracker from '@/components/layout/RouteAnalyticsTracker';

import Community from '@/pages/Community';
import News from '@/pages/News';
import Purchase from '@/pages/Purchase';
import Downloads from '@/pages/Downloads';
import BugReports from '@/pages/BugReports';
import Docs from '@/pages/Docs';
import DocsApps from '@/pages/DocsApps';
import DocsAppPage from '@/pages/DocsAppPage';
import DocsConceptPage from '@/pages/DocsConceptPage';
import DocsUseCasePage from '@/pages/DocsUseCasePage';
import NotFound from '@/pages/NotFound';
import LandingPage from '@/pages/LandingPage';
import Partnership from '@/pages/Partnership';
import ProductDetails from '@/pages/ProductDetails';
import WhatIsMapleOS from '@/pages/WhatIsMapleOS';
import AIOperatingSystem from '@/pages/AIOperatingSystem';
import AISurfaces from '@/pages/AISurfaces';
import AIBlueprints from '@/pages/AIBlueprints';
import AICenter from '@/pages/AICenter';
import MapleOSForConsumers from '@/pages/MapleOSForConsumers';
import MapleOSForDevelopers from '@/pages/MapleOSForDevelopers';
import MapleOSVsWindows from '@/pages/MapleOSVsWindows';
import MapleOSVsChatGPT from '@/pages/MapleOSVsChatGPT';
import MapleOSVsChromeOS from '@/pages/MapleOSVsChromeOS';

const TitleBar = lazy(() => import('@/components/TitleBar').then((module) => ({ default: module.TitleBar })));
const MapleAppShell = lazy(() => import('@/components/layout/MapleAppShell'));
const MainLayout = lazy(() => import('@/components/layout/MainLayout'));

const Index = lazy(() => import('@/pages/Index'));
const Auth = lazy(() => import('@/pages/Auth'));
const Chat = lazy(() => import('@/pages/Chat'));
const Profile = lazy(() => import('@/pages/Profile'));
const Settings = lazy(() => import('@/pages/Settings'));
const KnowledgeBase = lazy(() => import('@/pages/KnowledgeBase'));
const Projects = lazy(() => import('@/pages/Projects'));
const ProjectDetail = lazy(() => import('@/pages/ProjectDetail'));
const Code = lazy(() => import('@/pages/Code'));
const Canvas = lazy(() => import('@/pages/Canvas'));
const Users = lazy(() => import('@/pages/Users'));
const Lobby = lazy(() => import('@/pages/Lobby'));
const NewsDetail = lazy(() => import('@/pages/NewsDetail'));
const About = lazy(() => import('@/pages/About'));
const Eula = lazy(() => import('@/pages/Eula'));
const MCPServices = lazy(() => import('@/pages/MCPServices'));
const Data = lazy(() => import('@/pages/Data'));
const AgentMarketplace = lazy(() => import('@/pages/agents/AgentMarketplace'));
const ProfessionalAgents = lazy(() => import('@/pages/agents/ProfessionalAgents'));
const ScientificAgents = lazy(() => import('@/pages/agents/ScientificAgents'));
const CustomAgent = lazy(() => import('@/pages/agents/CustomAgent'));
const UseAgent = lazy(() => import('@/pages/agents/UseAgent'));
const MaplePT = lazy(() => import('@/pages/MaplePT'));
const CompleteRegistrationPage = lazy(() => import('@/pages/CompleteRegistration'));
const AccessControl = lazy(() => import('@/pages/AccessControl'));
const AICredits = lazy(() => import('@/pages/AICredits'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const ChangelogPage = lazy(() => import('@/pages/Changelog'));
const WelcomePage = lazy(() => import('@/pages/WelcomePage'));
const Research = lazy(() => import('@/pages/Research'));
const MapleOSStart = lazy(() => import('@/pages/MapleOSStart'));
const IntelligenceXP = lazy(() => import('@/pages/IntelligenceXP'));
const WebSettings = lazy(() => import('@/pages/WebSettings'));
const WebProfile = lazy(() => import('@/pages/WebProfile'));
const Admin = lazy(() => import('@/pages/Admin'));
const MapleMarket = lazy(() => import('@/pages/MapleMarket'));
const MapleMarketAppDetail = lazy(() => import('@/pages/MapleMarketAppDetail'));
const MapleMarketLibrary = lazy(() => import('@/pages/MapleMarketLibrary'));

function RouteFallback() {
  return null;
}

function ClientOnly({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

function ProtectedElement({ children }: { children: React.ReactNode }) {
  return <ProtectedRoute>{children}</ProtectedRoute>;
}

function renderPublicRoutes(isElectron: boolean) {
  return (
    <>
      <Route element={<PublicSiteShell />}>
        <Route path="/" element={isElectron ? <Navigate to="/auth" replace /> : <LandingPage />} />
        <Route path="/products" element={<Purchase />} />
        <Route path="/products/:productId" element={<ProductDetails />} />
        <Route path="/news" element={<News />} />
        <Route path="/forums" element={<Community />} />
        <Route path="/bugs" element={<BugReports />} />
        <Route path="/partnership" element={<Partnership />} />
        <Route path="/what-is-mapleos" element={<WhatIsMapleOS />} />
        <Route path="/ai-operating-system" element={<AIOperatingSystem />} />
        <Route path="/ai-surfaces" element={<AISurfaces />} />
        <Route path="/ai-blueprints" element={<AIBlueprints />} />
        <Route path="/ai-center" element={<AICenter />} />
        <Route path="/mapleos-for-consumers" element={<MapleOSForConsumers />} />
        <Route path="/mapleos-for-developers" element={<MapleOSForDevelopers />} />
        <Route path="/mapleos-vs-windows" element={<MapleOSVsWindows />} />
        <Route path="/mapleos-vs-chatgpt" element={<MapleOSVsChatGPT />} />
        <Route path="/mapleos-vs-chromeos" element={<MapleOSVsChromeOS />} />
        <Route path="/docs" element={<Docs />} />
        <Route path="/docs/apps" element={<DocsApps />} />
        <Route path="/docs/apps/:docSlug" element={<DocsAppPage />} />
        <Route path="/docs/concepts/:conceptSlug" element={<DocsConceptPage />} />
        <Route path="/docs/use-cases/:useCaseSlug" element={<DocsUseCasePage />} />
        <Route path="/docs/:sectionId" element={<Navigate to="/docs" replace />} />
        <Route path="/downloads" element={<Downloads />} />
        <Route path="/community" element={<Navigate to="/forums" replace />} />
        <Route path="/bug-reports" element={<Navigate to="/bugs" replace />} />
      </Route>
    </>
  );
}

function PublicRoutes({ isElectron }: { isElectron: boolean }) {
  return (
    <Routes>
      {renderPublicRoutes(isElectron)}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function BrowserAppRoutes() {
  const isElectron = typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().includes('electron');

  return (
    <Routes>
      <Route path="/auth" element={<ClientOnly><Auth /></ClientOnly>} />
      <Route path="/complete-registration" element={<ClientOnly><CompleteRegistrationPage /></ClientOnly>} />

      {renderPublicRoutes(isElectron)}

      <Route element={<ClientOnly><MapleAppShell /></ClientOnly>}>
        <Route path="/market" element={<ClientOnly><MapleMarket /></ClientOnly>} />
        <Route path="/market/library" element={<ClientOnly><MapleMarketLibrary /></ClientOnly>} />
        <Route path="/market/library/:appId" element={<ClientOnly><MapleMarketLibrary /></ClientOnly>} />
        <Route path="/market/app/:appId" element={<ClientOnly><MapleMarketAppDetail /></ClientOnly>} />
        <Route path="/market/help" element={<Navigate to="/market?page=help" replace />} />
        <Route path="/market/contact" element={<Navigate to="/market?page=contact" replace />} />
        <Route path="/market/refund-policy" element={<Navigate to="/market?page=refund-policy" replace />} />
        <Route path="/market/developers" element={<Navigate to="/market?page=developers" replace />} />
        <Route path="/market/developers/api" element={<Navigate to="/market?page=developers-api" replace />} />
        <Route path="/market/submit-app" element={<Navigate to="/market?page=submit-app" replace />} />
        <Route path="/market/legal/eula" element={<Navigate to="/market?page=legal-eula" replace />} />
        <Route path="/market/legal/privacy" element={<Navigate to="/market?page=legal-privacy" replace />} />
        <Route path="/market/legal/data" element={<Navigate to="/market?page=legal-data" replace />} />

        <Route path="/chats" element={<ProtectedElement><ClientOnly><Index /></ClientOnly></ProtectedElement>} />
        <Route path="/chat/:chatId" element={<ProtectedElement><ClientOnly><Chat /></ClientOnly></ProtectedElement>} />
        <Route path="/lobby" element={<ProtectedElement><ClientOnly><Lobby /></ClientOnly></ProtectedElement>} />
        <Route path="/lobby/:channelSlug" element={<ProtectedElement><ClientOnly><Lobby /></ClientOnly></ProtectedElement>} />
        <Route path="/projects" element={<ProtectedElement><ClientOnly><Projects /></ClientOnly></ProtectedElement>} />
        <Route path="/projects/:id" element={<ProtectedElement><ClientOnly><ProjectDetail /></ClientOnly></ProtectedElement>} />
        <Route path="/canvas" element={<ProtectedElement><ClientOnly><Canvas /></ClientOnly></ProtectedElement>} />
        <Route path="/code" element={<ProtectedElement><ClientOnly><Code /></ClientOnly></ProtectedElement>} />
        <Route path="/knowledge" element={<ProtectedElement><ClientOnly><KnowledgeBase /></ClientOnly></ProtectedElement>} />
        <Route path="/kb" element={<ProtectedElement><Navigate to="/knowledge" replace /></ProtectedElement>} />
        <Route path="/mcp" element={<ProtectedElement><ClientOnly><MCPServices /></ClientOnly></ProtectedElement>} />
        <Route path="/users" element={<ProtectedElement><ClientOnly><Users /></ClientOnly></ProtectedElement>} />
        <Route path="/settings" element={<ProtectedElement><ClientOnly><Settings /></ClientOnly></ProtectedElement>} />
        <Route path="/profile" element={<ProtectedElement><ClientOnly><Profile /></ClientOnly></ProtectedElement>} />
        <Route path="/agents" element={<ProtectedElement><ClientOnly><AgentMarketplace /></ClientOnly></ProtectedElement>} />
        <Route path="/agents/custom" element={<ProtectedElement><ClientOnly><CustomAgent /></ClientOnly></ProtectedElement>} />
        <Route path="/agents/professional" element={<ProtectedElement><ClientOnly><ProfessionalAgents /></ClientOnly></ProtectedElement>} />
        <Route path="/agents/scientific" element={<ProtectedElement><ClientOnly><ScientificAgents /></ClientOnly></ProtectedElement>} />
        <Route path="/agents/use" element={<ProtectedElement><ClientOnly><UseAgent /></ClientOnly></ProtectedElement>} />
        <Route path="/purchase" element={<ClientOnly><Purchase /></ClientOnly>} />
        <Route path="/credits" element={<ClientOnly><AICredits /></ClientOnly>} />
        <Route path="/news/:slug" element={<ClientOnly><NewsDetail /></ClientOnly>} />
        <Route path="/data-policy" element={<ClientOnly><Data /></ClientOnly>} />
        <Route path="/privacy" element={<ClientOnly><Privacy /></ClientOnly>} />
        <Route path="/eula" element={<ClientOnly><Eula /></ClientOnly>} />
        <Route path="/about" element={<ClientOnly><About /></ClientOnly>} />
        <Route path="/changelog" element={<ClientOnly><ChangelogPage /></ClientOnly>} />
        <Route path="/welcome" element={<ClientOnly><WelcomePage /></ClientOnly>} />
        <Route path="/research" element={<ClientOnly><Research /></ClientOnly>} />
        <Route path="/maplept" element={<ClientOnly><MaplePT /></ClientOnly>} />
        <Route path="/desktop/start/*" element={<ClientOnly><MapleOSStart /></ClientOnly>} />
        <Route path="/start/*" element={<ClientOnly><MapleOSStart /></ClientOnly>} />
        <Route path="/access-control" element={<ClientOnly><AccessControl /></ClientOnly>} />
        <Route path="/intelligence-xp" element={<ClientOnly><IntelligenceXP /></ClientOnly>} />
        <Route path="/web-settings" element={<ClientOnly><MainLayout><WebSettings /></MainLayout></ClientOnly>} />
        <Route path="/admin" element={<ProtectedElement><ClientOnly><Admin /></ClientOnly></ProtectedElement>} />
        <Route path="/web-profile" element={<ClientOnly><MainLayout><WebProfile /></MainLayout></ClientOnly>} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function BrowserApp() {
  return (
    <AuthProvider>
      <UserMessagesProvider>
        <LocalLLMProvider>
          <ClientOnly>
            <TitleBar />
          </ClientOnly>
          <BrowserAppRoutes />
          <Toaster />
        </LocalLLMProvider>
      </UserMessagesProvider>
    </AuthProvider>
  );
}

function PublicApp({ isElectron }: { isElectron: boolean }) {
  return (
    <AuthProvider>
      <PublicRoutes isElectron={isElectron} />
    </AuthProvider>
  );
}

function App({ ssrPayload = null }: { ssrPayload?: SsrPayload | null }) {
  const location = useLocation();
  const isElectron = typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().includes('electron');
  const shouldRenderPublicApp = isPublicSsrPath(location.pathname);

  return (
    <SsrPayloadProvider payload={ssrPayload}>
      <RouteAnalyticsTracker />
      {shouldRenderPublicApp ? <PublicApp isElectron={isElectron} /> : <BrowserApp />}
    </SsrPayloadProvider>
  );
}

export default App;